import baseApi from './RootBaseApi';
import {
  BoatResponse,
  BoatListQueryParams,
  BoatPayload,
} from '@/types/boat-management.types';
import { PaginatedApiResponse } from '@/types/api.types';

export const ClubBoatManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getClubBoats: builder.query<
      PaginatedApiResponse<BoatResponse[]>,
      BoatListQueryParams & { club_id: string }
    >({
      query: ({ page, limit, search, type, club_id }) => {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          club_id,
        });
        if (search) params.append('search', search);
        if (type) params.append('type', type);
        return {
          url: 'club-boats',
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: PaginatedApiResponse<BoatResponse[]>) =>
        response,
      providesTags: [{ type: 'Boats', id: 'CLUB_LIST' }],
      keepUnusedDataFor: 0,
    }),

    createClubBoat: builder.mutation<BoatResponse, BoatPayload>({
      query: (data) => ({
        url: 'club-boats',
        method: 'POST',
        data,
      }),
      transformResponse: (response: BoatResponse) => response,
      invalidatesTags: [
        { type: 'Boats', id: 'CLUB_LIST' },
        { type: 'ClubsMembers' },
      ],
    }),

    updateClubBoat: builder.mutation<
      BoatResponse,
      { boat_id: string; data: BoatPayload }
    >({
      query: ({ boat_id, data }) => ({
        url: `club-boats/${boat_id}`,
        method: 'PATCH',
        data,
      }),
      transformResponse: (response: BoatResponse) => response,
      invalidatesTags: (result, error, { boat_id }) => [
        { type: 'Boats', id: 'CLUB_LIST' },
        { type: 'Boats', id: boat_id },
        { type: 'ClubsMembers' },
      ],
    }),

    deleteClubBoat: builder.mutation<void, string>({
      query: (boat_id) => ({
        url: `club-boats/${boat_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, boat_id) => [
        { type: 'Boats', id: 'CLUB_LIST' },
        { type: 'Boats', id: boat_id },
        { type: 'ClubsMembers' },
      ],
    }),

    getClubBoatById: builder.query<BoatResponse, string>({
      query: (boatId) => ({
        url: `club-boats/${boatId}`,
        method: 'GET',
      }),
      transformResponse: (response: BoatResponse) => response,
      providesTags: (result, error, boatId) => [{ type: 'Boats', id: boatId }],
      keepUnusedDataFor: 0,
    }),

    getMemberLinkedClubBoats: builder.query<
      PaginatedApiResponse<BoatResponse[]>,
      {
        member_id: string;
        page?: number;
        limit?: number;
        search?: string;
      }
    >({
      query: ({ member_id, page, limit, search }) => {
        const params = new URLSearchParams({});
        if (page) params.append('page', String(page));
        if (limit) params.append('limit', String(limit));
        if (search) params.append('search', search);
        return {
          url: `club-boats/member/${member_id}/linked`,
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: PaginatedApiResponse<BoatResponse[]>) =>
        response,
      providesTags: [{ type: 'Boats', id: 'CLUB_LIST' }],
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetClubBoatsQuery,
  useGetMemberLinkedClubBoatsQuery,
  useCreateClubBoatMutation,
  useUpdateClubBoatMutation,
  useDeleteClubBoatMutation,
  useGetClubBoatByIdQuery,
} = ClubBoatManagementService;
